import {
  makeStyles,
  tableHeaderCellClassNames,
  tokens,
} from '@fluentui/react-components';

export const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  divider: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },

  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
  },

  sectionTitleText: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },

  // KPI cards
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingHorizontalM,
  },

  kpiCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
  },

  kpiValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
    lineHeight: tokens.lineHeightHero700,
  },

  kpiCategory: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },

  kpiLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },

  kpiLabelRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },

  infoIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },

  // Breakdown bars (by category / by application)
  breakdownGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalXXL,
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },

  breakdownCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  breakdownTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },

  breakdownRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(80px, 140px) 1fr auto',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
  },

  breakdownLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  progressBar: {
    width: '100%',
  },

  breakdownCount: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'right',
    minWidth: '24px',
  },

  // Weekly trend chart (zero-filled 7-day buckets, rendered as inline SVG)
  trendCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  trendSvg: {
    display: 'block',
    width: '100%',
    height: 'auto',
    maxHeight: '220px',
  },

  axisLine: {
    stroke: tokens.colorNeutralStroke2,
    strokeWidth: '1',
  },

  gridLine: {
    stroke: tokens.colorNeutralStroke3,
    strokeWidth: '1',
    strokeDasharray: '3 3',
  },

  axisText: {
    fill: tokens.colorNeutralForeground3,
  },

  valueText: {
    fill: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },

  trendBar: {
    fill: tokens.colorBrandBackground,
  },

  // Table
  table: {
    width: '100%',
  },

  // Fluent renders a native table with `table-layout: fixed`, so column widths
  // come from these header-cell widths (flex properties are ignored in native
  // mode); unset columns (Action, Application) share the remaining space.
  colDate: {
    width: '116px',
  },

  colCategory: {
    width: '112px',
  },

  colFlex: {
    overflow: 'hidden',
  },

  colCount: {
    width: '96px',
  },

  colUnique: {
    width: '124px',
  },

  appBadge: {
    display: 'inline-block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },

  // Native table cells are `display: table-cell`, so right-alignment must use
  // `textAlign` — `justifyContent` only applies in the flex (noNativeElements)
  // layout. This lines the values up under their right-aligned headers.
  numericCell: {
    textAlign: 'right',
  },

  numericHeader: {
    textAlign: 'right',
    [`& .${tableHeaderCellClassNames.button}`]: {
      justifyContent: 'flex-end',
      width: '100%',
    },
  },

  // State messages
  stateWrap: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },

  emptyState: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
});
